/**
 * @author       Pavlo Androshchuk
 * @copyright    Copyright (c) 2022, Zumper
 */

export interface MenuNavLink {
  name: string
  href: string
  is_indexed: boolean
}

export interface ExtraNavLink {
  name: string
  href: string
  title: string
  is_indexed: boolean
  trackingName: string
}

export interface LinksSubmenuProps {
  extraLinks?: readonly ExtraNavLink[]
  links: MenuNavLink[]
  getLocaleLinkTitle: (name: string) => string
  onLinkClick: (name: string, href: string) => void
}

interface Link {
  name: string
  url: string
  is_indexed: boolean
}

const highInventoryLowTrafficLinks = [
  {
    name: 'New York',
    url: 'new-york-ny',
    is_indexed: true,
  },
  {
    name: 'Chicago',
    url: 'chicago-il',
    is_indexed: true,
  },
  {
    name: 'Houston',
    url: 'houston-tx',
    is_indexed: true,
  },
  {
    name: 'Dallas',
    url: 'dallas-tx',
    is_indexed: true,
  },
  {
    name: 'Boston',
    url: 'boston-ma',
    is_indexed: true,
  },
  {
    name: 'San Antonio',
    url: 'san-antonio-tx',
    is_indexed: true,
  },
  {
    name: 'Phoenix',
    url: 'phoenix-az',
    is_indexed: true,
  },
  {
    name: 'Atlanta',
    url: 'atlanta-ga',
    is_indexed: true,
  },
  {
    name: 'Charlotte',
    url: 'charlotte-nc',
    is_indexed: true,
  },
] as const satisfies readonly Link[]

const afrLinks = [
  {
    name: 'San Francisco',
    url: 'san-francisco-ca',
    is_indexed: true,
  },
  {
    name: 'Oakland',
    url: 'oakland-ca',
    is_indexed: true,
  },
  {
    name: 'Seattle',
    url: 'seattle-wa',
    is_indexed: true,
  },
  {
    name: 'Los Angeles',
    url: 'los-angeles-ca',
    is_indexed: true,
  },
  {
    name: 'Philadelphia',
    url: 'philadelphia-pa',
    is_indexed: true,
  },
  {
    name: 'Washington',
    url: 'washington-dc',
    is_indexed: true,
  },
  {
    name: 'Austin',
    url: 'austin-tx',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const hfrLinks = [
  {
    name: 'San Francisco',
    url: 'san-francisco-ca',
    is_indexed: true,
  },
  {
    name: 'Oakland',
    url: 'oakland-ca',
    is_indexed: true,
  },
  {
    name: 'Seattle',
    url: 'seattle-wa',
    is_indexed: true,
  },
  {
    name: 'Los Angeles',
    url: 'los-angeles-ca',
    is_indexed: true,
  },
  {
    name: 'New York',
    url: 'new-york-ny',
    is_indexed: true,
  },
  {
    name: 'Washington',
    url: 'washington-dc',
    is_indexed: true,
  },
  {
    name: 'Austin',
    url: 'austin-tx',
    is_indexed: true,
  },
  {
    name: 'Philadelphia',
    url: 'philadelphia-pa',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks.slice(1),
] as const satisfies readonly Link[]

const rfrLinks = [
  {
    name: 'Oakland',
    url: 'oakland-ca',
    is_indexed: true,
  },
  {
    name: 'Seattle',
    url: 'seattle-wa',
    is_indexed: true,
  },
  {
    name: 'Los Angeles',
    url: 'los-angeles-ca',
    is_indexed: true,
  },
  {
    name: 'Philadelphia',
    url: 'philadelphia-pa',
    is_indexed: true,
  },
  {
    name: 'Washington',
    url: 'washington-dc',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const cfrLinks = [
  {
    name: 'San Francisco',
    url: 'san-francisco-ca',
    is_indexed: true,
  },
  {
    name: 'Oakland',
    url: 'oakland-ca',
    is_indexed: true,
  },
  {
    name: 'Seattle',
    url: 'seattle-wa',
    is_indexed: true,
  },
  {
    name: 'Los Angeles',
    url: 'los-angeles-ca',
    is_indexed: true,
  },
  {
    name: 'Philadelphia',
    url: 'philadelphia-pa',
    is_indexed: true,
  },
  {
    name: 'Washington',
    url: 'washington-dc',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const afrShortTermLinks = [
  {
    name: 'Miami',
    url: 'miami-fl/short-term',
    is_indexed: true,
  },
  {
    name: 'Los Angeles',
    url: 'los-angeles-ca/short-term',
    is_indexed: true,
  },
  {
    name: 'San Diego',
    url: 'san-diego-ca/short-term',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const afrLuxuryLinks = [
  {
    name: 'San Francisco',
    url: 'san-francisco-ca/luxury',
    is_indexed: true,
  },
  {
    name: 'Seattle',
    url: 'seattle-wa/luxury',
    is_indexed: true,
  },
  {
    name: 'Denver',
    url: 'denver-co/luxury',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const afrPetFriendlyLinks = [
  {
    name: 'Colorado Springs',
    url: 'colorado-springs-co/pet-friendly',
    is_indexed: true,
  },
  {
    name: 'Boston',
    url: 'boston-ma/pet-friendly',
    is_indexed: true,
  },
  {
    name: 'Philadelphia',
    url: 'philadelphia-pa/pet-friendly',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

const afrCheapLinks = [
  {
    name: 'Miami',
    url: 'miami-fl/cheap',
    is_indexed: true,
  },
  {
    name: 'Austin',
    url: 'austin-tx/cheap',
    is_indexed: true,
  },
  {
    name: 'Brooklyn',
    url: 'brooklyn-ny/cheap',
    is_indexed: true,
  },
  ...highInventoryLowTrafficLinks,
] as const satisfies readonly Link[]

export const getMenuNavLinks = (
  urlPrefix: `/${'apartments' | 'condos' | 'houses' | 'rooms'}-for-rent/`,
  links: readonly Link[],
  maxLinks: number,
  pathName: string
) => {
  let result = [...links]
  if (pathName) {
    result = result.filter((l) => !pathName.startsWith(`${urlPrefix}${l.url}`))
  }
  if (maxLinks !== undefined) {
    result = result.slice(0, maxLinks)
  }

  return result.map(
    ({ name, url, is_indexed }) =>
      ({
        name,
        href: `${urlPrefix}${url}`,
        is_indexed,
      }) as const
  ) satisfies MenuNavLink[]
}

export const getAFRMenuNavDefaultLinks = (pathName: string) =>
  getMenuNavLinks('/apartments-for-rent/', afrLinks, 15, pathName)

export const getHFRMenuNavDefaultLinks = (pathName: string) =>
  getMenuNavLinks('/houses-for-rent/', hfrLinks, 15, pathName)

export const getRFRMenuNavDefaulLinks = (pathName: string) =>
  getMenuNavLinks('/rooms-for-rent/', rfrLinks, 11, pathName)

export const getCFRMenuNavDefaultLinks = (pathName: string) =>
  getMenuNavLinks('/condos-for-rent/', cfrLinks, 10, pathName)

export const getAFRShortTermMenuNavLinks = (pathName: string) =>
  getMenuNavLinks('/apartments-for-rent/', afrShortTermLinks, 3, pathName)

export const getAFRLuxuryMenuNavLinks = (pathName: string) =>
  getMenuNavLinks('/apartments-for-rent/', afrLuxuryLinks, 3, pathName)

export const getAFRPetFriendlyMenuNavLinks = (pathName: string) =>
  getMenuNavLinks('/apartments-for-rent/', afrPetFriendlyLinks, 3, pathName)

export const getAFRCheapMenuNavLinks = (pathName: string) =>
  getMenuNavLinks('/apartments-for-rent/', afrCheapLinks, 3, pathName)
